import type { TSinchToastType } from "@nectary/components/toast/types";
import * as bus from "@sinch/bus";
import type {
  BasicToastMessage,
  ClearToastMessage,
  CustomToastMessage,
  ToastId,
} from "@sinch/bus";
import { useCallback, useEffect, useReducer } from "react";
import { useIntl } from "react-intl";
import type { IntlShape } from "react-intl";

export type Toast = {
  id: ToastId;
  text: string;
  type: TSinchToastType;
  closable: boolean;
  persistent: boolean;
};

const warnIfPersistsForever = (payload: CustomToastMessage["payload"]) => {
  if (payload.persistent && !payload.closable) {
    console.warn(
      `This toast (${payload.id}) will persist forever, please make sure you eventually clear it with the clearToast() function`
    );
  }
};
type NewCustomToast = (payload: CustomToastMessage["payload"]) => Toast;
const newCustomToast: NewCustomToast = (payload) => {
  warnIfPersistsForever(payload);
  const { closable = true, persistent = false } = payload;
  return {
    ...payload,
    closable,
    persistent,
  };
};

const exhaustiveBasicToastTypeCheck = (type: never) => {
  console.error(`Basic toast type: ${type} is not handled`);
};

type NewBasicToast = (
  payload: BasicToastMessage["payload"],
  intl: IntlShape
) => Toast | null;
const newBasicToast: NewBasicToast = (payload, intl) => {
  switch (payload.type) {
    case "success:saved":
      return {
        id: payload.id,
        text: intl.formatMessage({
          id: "toastBasicSuccessSaved",
        }),
        type: "success",
        closable: true,
        persistent: false,
      };
    case "error:saving":
      return {
        id: payload.id,
        text: intl.formatMessage({
          id: "toastBasicErrorSaving",
        }),
        type: "error",
        closable: true,
        persistent: true,
      };
    default:
      exhaustiveBasicToastTypeCheck(payload.type);
      return null;
  }
};

const exhaustivePayloadActionCheck = (type: never) => {
  console.error(`Toast action (${type}) is not handled`);
};

type ReducerWithIntl = (
  intl: IntlShape
) => (
  toasts: Toast[],
  message: BasicToastMessage | CustomToastMessage | ClearToastMessage
) => Toast[];
const reducerWithIntl: ReducerWithIntl =
  (intl) =>
  (toasts, { type, payload }) => {
    switch (type) {
      case "BASIC_TOAST": {
        const toast = newBasicToast(payload, intl);
        return toast ? toasts.concat(toast) : toasts;
      }
      case "CUSTOM_TOAST": {
        const toast = newCustomToast(payload);
        return toasts.concat(toast);
      }
      case "CLEAR_TOAST":
        return toasts.filter((toast) => toast.id !== payload.id);
      default:
        exhaustivePayloadActionCheck(type);
        return toasts;
    }
  };

type CloseFuntionInitiator = (
  id: ToastId
) => (method: bus.ToastClosedMethod) => void;

type UseToastHandler = () => {
  toasts: Toast[];
  onClose: CloseFuntionInitiator;
};

export const useToastHandler: UseToastHandler = () => {
  const intl = useIntl();
  const [toasts, dispatch] = useReducer(reducerWithIntl(intl), []);

  useEffect(
    () =>
      bus.listen(
        (message) => bus.isData(message) && dispatch(message),
        bus.isToastMessage
      ),
    []
  );

  const onClose = useCallback<CloseFuntionInitiator>(
    (id: ToastId) => (method) => {
      dispatch({ type: "CLEAR_TOAST", payload: { id } });
      // Communicate with MFEs that this toast was closed
      bus.sendLocal(bus.toastClosedMessage(id, method));
    },
    []
  );

  return { toasts, onClose };
};

export const useToastClearedCallback = (
  id: ToastId,
  onClose: ReturnType<CloseFuntionInitiator>
) => {
  useEffect(() => {
    const unsubscribe = bus.listen((message) => {
      if (bus.isData(message) && message.payload.id === id) {
        onClose("system");
      }
    }, bus.isClearToastMessage);

    return unsubscribe;
  }, [id, onClose]);
};
